import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  FaCalendarAlt, FaVideo, FaPlus, FaChevronLeft, FaChevronRight,
  FaTrash, FaTimes, FaClock, FaLink,
} from 'react-icons/fa';
import { UserContext } from '../../App';
import './Calendario.css';

const BASE = (process.env.REACT_APP_APIURL || 'http://localhost:4000').replace(/\/$/, '');
const api  = (p) => `${BASE}/${p}`;

const DAYS   = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  let dow = first.getDay() - 1;
  if (dow < 0) dow = 6;

  const days = [];
  for (let i = 0; i < dow; i++)
    days.push({ d: new Date(year, month, i - dow + 1), cur: false });
  for (let d = 1; d <= last.getDate(); d++)
    days.push({ d: new Date(year, month, d), cur: true });
  let next = 1;
  while (days.length % 7 !== 0)
    days.push({ d: new Date(year, month + 1, next++), cur: false });
  return days;
}

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtFull = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) + 'h';
};

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

const Toast = ({ msg, type }) => (
  <div className={`cal-toast cal-toast--${type}`}>{msg}</div>
);

export default function Calendario() {
  const user  = useContext(UserContext);
  const today = new Date();

  const [yr, setYr]         = useState(today.getFullYear());
  const [mo, setMo]         = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selEvent, setSelEvent] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm] = useState({ titulo: '', descripcion: '', fecha: '', hora: '10:00', zoom_url: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    try {
      const d = await fetch(api('mentoria')).then(r => r.json());
      setEvents(Array.isArray(d) ? d : []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const days = buildGrid(yr, mo);

  const eventsOn = (date) => {
    const k = dateKey(date);
    return events.filter(e => e.fecha_hora.slice(0, 10) === k);
  };

  const upcoming = events
    .filter(e => new Date(e.fecha_hora) >= today)
    .sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora))
    .slice(0, 8);

  const prevMo = () => mo === 0  ? (setYr(y => y - 1), setMo(11)) : setMo(m => m - 1);
  const nextMo = () => mo === 11 ? (setYr(y => y + 1), setMo(0))  : setMo(m => m + 1);

  const isToday = (d) =>
    d.getDate()     === today.getDate() &&
    d.getMonth()    === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  const submit = async () => {
    if (!form.titulo || !form.fecha || !form.hora || !form.zoom_url)
      return flash('Rellena título, fecha, hora y enlace Zoom', 'error');
    setSaving(true);
    try {
      const res = await fetch(api('mentoria'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: form.titulo,
          descripcion: form.descripcion,
          fecha_hora: `${form.fecha}T${form.hora}:00`,
          zoom_url: form.zoom_url,
          created_by: user?.id,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setShowAdd(false);
      setForm({ titulo: '', descripcion: '', fecha: '', hora: '10:00', zoom_url: '' });
      flash('Mentoría creada. Todos los alumnos han sido notificados.');
      load();
    } catch (e) { flash(e.message || 'Error guardando', 'error'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta mentoría?')) return;
    try {
      await fetch(api(`mentoria/${id}`), { method: 'DELETE' });
      setSelEvent(null);
      flash('Mentoría eliminada');
      load();
    } catch { flash('Error eliminando', 'error'); }
  };

  if (loading) return <div className="cal-page cal-page--loading"><div className="cal-spinner" /></div>;

  return (
    <div className="cal-page">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* ── Page header ── */}
      <div className="cal-header">
        <div className="cal-title">
          <FaCalendarAlt className="cal-title-icon" />
          <div>
            <h1>Mentorías</h1>
            <p>Sesiones en vivo con el equipo de Closer Academy</p>
          </div>
        </div>
        {user?.firstRole && (
          <button className="cal-add-btn" onClick={() => setShowAdd(true)}>
            <FaPlus /> Añadir mentoría
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="cal-body">

        {/* Calendar grid */}
        <div className="cal-grid-wrap">
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMo}><FaChevronLeft /></button>
            <span className="cal-month-label">{MONTHS[mo]} {yr}</span>
            <button className="cal-nav-btn" onClick={nextMo}><FaChevronRight /></button>
          </div>

          <div className="cal-grid">
            {DAYS.map(d => <div key={d} className="cal-dow">{d}</div>)}
            {days.map(({ d, cur }, i) => {
              const evs = eventsOn(d);
              return (
                <div
                  key={i}
                  className={[
                    'cal-cell',
                    !cur      ? 'cal-cell--out'       : '',
                    isToday(d) ? 'cal-cell--today'     : '',
                    evs.length ? 'cal-cell--has-event'  : '',
                  ].join(' ')}
                >
                  <span className="cal-day-num">{d.getDate()}</span>
                  {evs.map(ev => (
                    <button
                      key={ev.id}
                      className="cal-chip"
                      onClick={() => setSelEvent(ev)}
                      title={ev.titulo}
                    >
                      <FaVideo className="cal-chip-icon" />
                      <span>{fmtTime(ev.fecha_hora)} {ev.titulo}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming sidebar */}
        <aside className="cal-upcoming">
          <h3 className="cal-upcoming-title">Próximas sesiones</h3>
          {upcoming.length === 0 ? (
            <p className="cal-upcoming-empty">No hay sesiones programadas.</p>
          ) : upcoming.map(ev => (
            <div key={ev.id} className="cal-upcoming-card" onClick={() => setSelEvent(ev)}>
              <div className="cal-upcoming-date">
                <span className="cal-upcoming-day">{new Date(ev.fecha_hora).getDate()}</span>
                <span className="cal-upcoming-mon">{MONTHS[new Date(ev.fecha_hora).getMonth()].slice(0, 3)}</span>
              </div>
              <div className="cal-upcoming-info">
                <div className="cal-upcoming-name">{ev.titulo}</div>
                <div className="cal-upcoming-time">
                  <FaClock /> {fmtTime(ev.fecha_hora)}h
                </div>
              </div>
              <FaVideo className="cal-upcoming-icon" />
            </div>
          ))}
        </aside>
      </div>

      {/* ── Event detail modal ── */}
      {selEvent && (
        <div className="cal-overlay" onClick={() => setSelEvent(null)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()}>
            <button className="cal-modal-close" onClick={() => setSelEvent(null)}><FaTimes /></button>
            <div className="cal-modal-icon-wrap"><FaVideo /></div>
            <h2 className="cal-modal-title">{selEvent.titulo}</h2>
            {selEvent.descripcion && <p className="cal-modal-desc">{selEvent.descripcion}</p>}
            <div className="cal-modal-meta">
              <FaClock /><span>{fmtFull(selEvent.fecha_hora)}</span>
            </div>
            <a href={selEvent.zoom_url} target="_blank" rel="noreferrer" className="cal-zoom-btn">
              <FaVideo /> Unirse a la mentoría
            </a>
            {user?.firstRole && (
              <button className="cal-del-btn" onClick={() => del(selEvent.id)}>
                <FaTrash /> Eliminar mentoría
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Add form modal ── */}
      {showAdd && (
        <div className="cal-overlay" onClick={() => setShowAdd(false)}>
          <div className="cal-modal cal-modal--form" onClick={e => e.stopPropagation()}>
            <button className="cal-modal-close" onClick={() => setShowAdd(false)}><FaTimes /></button>
            <h2 className="cal-modal-title">Nueva mentoría</h2>

            <div className="cal-form">
              <div className="cal-form-field">
                <label>Título *</label>
                <input
                  value={form.titulo}
                  onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                  placeholder="Ej: Sesión de cierre de ventas"
                />
              </div>
              <div className="cal-form-field">
                <label>Descripción</label>
                <textarea
                  value={form.descripcion}
                  onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                  placeholder="Breve descripción de la sesión (opcional)"
                  rows={3}
                />
              </div>
              <div className="cal-form-row">
                <div className="cal-form-field">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    value={form.fecha}
                    onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
                    min={today.toISOString().slice(0, 10)}
                  />
                </div>
                <div className="cal-form-field">
                  <label>Hora *</label>
                  <input
                    type="time"
                    value={form.hora}
                    onChange={e => setForm(p => ({ ...p, hora: e.target.value }))}
                  />
                </div>
              </div>
              <div className="cal-form-field">
                <label>Enlace Zoom *</label>
                <div className="cal-form-link-wrap">
                  <FaLink className="cal-form-link-icon" />
                  <input
                    value={form.zoom_url}
                    onChange={e => setForm(p => ({ ...p, zoom_url: e.target.value }))}
                    placeholder="https://zoom.us/j/..."
                    style={{ paddingLeft: '36px' }}
                  />
                </div>
              </div>
            </div>

            <div className="cal-form-actions">
              <button className="cal-btn cal-btn--ghost" onClick={() => setShowAdd(false)}>Cancelar</button>
              <button className="cal-btn cal-btn--primary" onClick={submit} disabled={saving}>
                {saving ? 'Guardando…' : <><FaPlus /> Crear y notificar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
